import { Button } from '@/components/ui/Button';
import { Popover, Transition } from '@headlessui/react';
import { Link } from 'gatsby';
import React, { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import signOutAction from '../../app/redux/actions/signOutAction';
import updateLastAction from '../../app/redux/actions/updateLastAction';
import { AppDispatch } from '../../app/redux/configureStore';
import { ActionType } from '../../app/services/authTypes';
import { siteLogoLink } from './utils/Logo';

import Loader from '@/components/ui/Loader';
import { getUserProfileDisplayName } from '@/lib/helpers/userProfile';
import { cn } from '@/lib/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { UserIcon } from '@heroicons/react/24/solid';
import { WindowLocation } from '@reach/router';
import { DimensionObject, UseDimensionsHook, getDimensionObject } from '../../app/hooks/useDimensions';
import { useAppSelector } from '../../app/redux/hooks';
import { UserMatch, UserMatchesState } from '../../app/redux/reducers/userMatchesReducer';
import { removeNonAlpaChars } from '../../app/utils/string';
import { ViewMessages, ViewMessagesCountBadge } from '../account/actions/ViewMessages';
import { UserProfileCompletionProgress } from '../account/UserProfile';
import UserMatches from '../notifications/userMatches/UserMatches';
import withLocation from '../withLocation';
import AccordionBlock from './accordionBlock';
import { ConditionalWrapper } from './utils/ConditionalWrapper';

const navigationHeightAdjustment = 60;

type NavigationItem = {
  id: string;
  text: string;
  external: boolean;
  weight: number;
  new: boolean;
  link: string;
  parentId?: string;
  children?: Array<NavigationItem>;
};

function useNavigationLayoutEffect(): UseDimensionsHook {
  const [dimensions, setDimensions] = useState<any>({});
  const [node, setNode] = useState<any>(null);

  const ref = useCallback((node: any) => {
    setNode(node);
  }, []);

  useLayoutEffect(() => {
    if (node) {
      const nodeEl = node as HTMLElement;
      const attrObserver = new MutationObserver((mutations) => {
        mutations.forEach((mu) => {
          if (mu.attributeName === 'data-headlessui-state') {
            const dimensionObject = getDimensionObject(nodeEl);
            setDimensions(dimensionObject);
            const parent: HTMLElement | null = nodeEl.closest('[data-depth="0"]');
            if (nodeEl.dataset && nodeEl.dataset.depth !== '0') {
              parent && (parent.style.height = `${dimensionObject.height}px`);
            }
          }
          return;
        });
      });
      attrObserver.observe(nodeEl, { attributes: true });
    }
  }, [node]);

  return [ref, dimensions, node];
}

interface NavigationProps {
  staticData: any;
  isSignedIn: boolean;
  signOut: any;
  updateAction: AppDispatch;
  authLevel: any;
  user: any;
  location: WindowLocation<unknown>;
}

const getNavigation = ({
  staticData,
  isSignedIn,
  signOut,
  updateAction,
  authLevel,
  user,
  location
}: NavigationProps) => {
  const { matches } = useAppSelector((state) => state.matches) as UserMatchesState;
  const numMatchesUnread = matches.filter((match: UserMatch) => !match.seen).length;
  const [navigationToggle, setNavigationToggle] = useState(false);

  useEffect(() => {
    if (navigationToggle) {
      window.document
        .querySelector('html')
        ?.classList.add(...['[&:has(#mobile-nav)]:global-scrollblock', 'lg:!global-scrollblock-none']);
    } else {
      window.document
        .querySelector('html')
        ?.classList.remove(...['[&:has(#mobile-nav)]:global-scrollblock', 'lg:!global-scrollblock-none']);
    }
  }, [navigationToggle]);

  /**
   * New flag markup
   */
  const newFlag = (
    <span className="uppercase text-gray hover text-xxs font-semibold bg-banana py-1 px-1.5 shadow-sm rounded absolute -mt-2 lg:top-0 lg:-right-10">
      New
    </span>
  );

  /**
   * mapNavigationData : Map the flat data into workable model for rendering.
   * @param list {Array<any} flat array of navigation data from CMS
   * @return Array<NavigationItem>
   */
  const mapNavigationData = (list: Array<any>): Array<NavigationItem> => {
    const mapNavigationItem = (item: any) => {
      return {
        id: item.id,
        text: item.title,
        weight: item.weight,
        new: item.field_is_new && item.field_is_new === '1' ? true : false,
        parentId: item.parent && item.parent.id ? item.parent.id : null,
        children: item.children,
        link: item.link && item.link.uri !== '<nolink>' ? item.link.uri : null,
        external: item.link && item.link.uri.indexOf('http') > -1 ? true : false
      } as NavigationItem;
    };

    const map: any = {};
    let node;
    const roots = [];
    let i;

    for (i = 0; i < list.length; i += 1) {
      map[list[i].id] = i; // initialize the map
      list[i].children = []; // initialize the children
    }

    for (i = 0; i < list.length; i += 1) {
      node = mapNavigationItem(list[i]);
      if (node.parentId) {
        // if you have dangling branches check that map[node.parentId] exists
        list[map[node.parentId]].children.push(node);
      } else {
        roots.push(node);
      }
    }
    return roots;
  };
  const navigationData: Array<NavigationItem> = mapNavigationData(
    staticData.allMenuLinkContent.nodes.sort((a: any, b: any) => a.weight - b.weight)
  );

  /**
   * navigationItemLink : renders internal vs external link
   * @param item {any}
   * @param classes {string}
   * @param onClick {any}
   * @return {JSX}
   */
  const navigationItemLink = (item: NavigationItem, classes: string = '', onClick: any = null) => {
    if (item.external) {
      return (
        <a
          href={item.link}
          className={`${classes} flex items-center`}
          target="_blank"
          onClick={onClick && onClick}
          data-cy={`link:view-${removeNonAlpaChars(item.text)}-site`}
          rel="noreferrer"
        >
          <span className="relative">
            {item.text}
            {item.new && newFlag}
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400 inline whitespace-nowrap ml-2" />
          </span>
        </a>
      );
    } else {
      return (
        <Link
          to={item.link ?? ''}
          className={`${classes} flex`}
          onClick={onClick && onClick}
          data-cy={`link:view-${removeNonAlpaChars(item.text)}`}
        >
          <span className="relative">
            {item.text}
            {item.new && newFlag}
          </span>
        </Link>
      );
    }
  };

  /**
   * Mobile navigation
   */

  const handleMobileNavigationItemClick = (event: any, to: string, updateAction: AppDispatch) => {
    setNavigationToggle(false);
    updateAction({ pathname: to, search: '' }, ActionType.VIEW);
  };

  const baseMobileLinkClasses =
    'text-base text-gray hover:text-primary transition-colors duration-200 relative ease-in-out';
  const navigationMobileRender = (
    <div className="lg:hidden relative" data-cy={`section:navigation`}>
      <Button
        icon
        id={'menu-toggle'}
        size="sm"
        appearance="primary"
        variant="outline"
        aria-label="Menu"
        onClick={() => setNavigationToggle(!navigationToggle)}
        data-cy={`button:toggle-navigation`}
      >
        <Bars3Icon className="h-4" />
      </Button>
      {isSignedIn && numMatchesUnread > 0 && (
        <ViewMessagesCountBadge className="absolute -top-1 -right-1">{numMatchesUnread}</ViewMessagesCountBadge>
      )}

      <Transition.Root show={navigationToggle} as={Fragment} appear={true}>
        <div className="relative z-[1]" id="mobile-nav" aria-label="menu" role="dialog" aria-modal="true">
          <div className="fixed inset-0 overflow-hidden" onClick={() => setNavigationToggle(false)}>
            <div
              className="pointer-events-auto fixed inset-y-0 right-0 flex w-screen max-w-full md:max-w-md"
              onClick={(e: any) => e.stopPropagation()}
            >
              <Transition.Child
                appear={true}
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className={cn('flex flex-col overflow-y-scroll pretty-scroll bg-white shadow-lg w-full h-full')}>
                  <div className="sticky top-0 bg-white z-[1]">
                    <div
                      className={`section-container-2xl flex justify-between items-center border-b border-deep-10 py-2 h-12 lg:h-16`}
                    >
                      <div className="md:hidden">
                        {siteLogoLink(staticData, 'w-fit', 'h-[32px]', false, undefined, isSignedIn)}
                      </div>
                      <div className="hidden md:block">&nbsp;</div>
                      <div className="flex space-x-2 items-center -mr-3">
                        <Button
                          icon
                          aria-label={'menu-toggle'}
                          appearance="gray"
                          size="sm"
                          onClick={() => setNavigationToggle(false)}
                          data-cy={`button:close-navigation`}
                        >
                          <XMarkIcon className="w-6" />
                        </Button>
                      </div>
                    </div>
                    {isSignedIn && (
                      <div>
                        {user && (
                          <Loader loaded={!!user} className="bg-gray-50 rounded-lg">
                            <div className="space-y-1 px-4 py-2">
                              <p className="text-gray font-bold leading-6">
                                {getUserProfileDisplayName(user, authLevel)}
                              </p>
                              <UserProfileCompletionProgress
                                user={user}
                                buttonProps={{ onClick: () => setNavigationToggle(false) }}
                              />
                            </div>
                          </Loader>
                        )}
                        <div className="bg-deep-50">
                          <div className="max-w-[450px] mx-auto md:max-w-none flex items-center justify-center space-x-9 h-32">
                            <div className="!flex flex-col space-y-1.5 justify-center items-center focus:outline-none w-[5.25rem]">
                              <Button asChild icon appearance="primary">
                                <Link
                                  to="/dashboard"
                                  onClick={(event: any) =>
                                    handleMobileNavigationItemClick(event, '/dashboard', updateAction)
                                  }
                                  data-cy={`link:view-feed`}
                                >
                                  <FontAwesomeIcon icon={['fas', 'home']} className="w-5" />
                                </Link>
                              </Button>
                              <p className="text-primary text-sm font-bold">Dashboard</p>
                            </div>

                            <div className="!flex flex-col space-y-1.5 justify-center items-center focus:outline-none w-[5.25rem]">
                              <ViewMessages icon appearance="primary" variant="default">
                                {(_numberOfMatches: number, numMatchesUnread: number) => {
                                  return (
                                    <>
                                      <FontAwesomeIcon icon={['fas', 'message']} className="w-5" />
                                      {numMatchesUnread > 0 && (
                                        <ViewMessagesCountBadge className="absolute -top-1 -right-1">
                                          {numMatchesUnread}
                                        </ViewMessagesCountBadge>
                                      )}
                                    </>
                                  );
                                }}
                              </ViewMessages>
                              <p className="text-primary text-sm font-bold">Messages</p>
                            </div>

                            <div
                              className={
                                '!flex flex-col space-y-1.5 justify-center items-center focus:outline-none w-[5.25rem]'
                              }
                            >
                              <Button asChild icon appearance="primary">
                                <Link
                                  to="/dashboard/profile"
                                  onClick={(event: any) =>
                                    handleMobileNavigationItemClick(event, '/dashboard/profile', updateAction)
                                  }
                                  data-cy={`link:view-profile`}
                                >
                                  <UserIcon className="w-5" />
                                </Link>
                              </Button>
                              <p className="text-primary text-sm font-bold">My profile</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <ul className="relative my-6 flex-1 px-3 space-y-8">
                    {navigationData.map((item: NavigationItem, index: number) => {
                      return (
                        <li key={`level1_${index}`}>
                          {item.link ? (
                            <p className="py-4">{navigationItemLink(item, `${baseMobileLinkClasses} font-bold`)}</p>
                          ) : (
                            <>
                              <h3 className="text-primary-700 font-semibold tracking-wider uppercase text-sm">
                                {item.text}
                              </h3>
                              {item.children && (
                                <ul className="divide-y divide-deep-100 pt-1 border-b border-deep-200">
                                  {item.children.map((child: NavigationItem, childIndex: number) => {
                                    return (
                                      <div key={`level2_${childIndex}`}>
                                        <li>
                                          {child.link ? (
                                            <p className="py-4">
                                              {navigationItemLink(child, `${baseMobileLinkClasses} font-bold`)}
                                            </p>
                                          ) : (
                                            <AccordionBlock
                                              content={[
                                                {
                                                  question: child.text,
                                                  answer: (
                                                    <ul className="space-y-5 leading-4 pt-5">
                                                      {child.children &&
                                                        child.children.map(
                                                          (subChild: NavigationItem, subChildIndex: number) => {
                                                            return (
                                                              subChild.link && (
                                                                <li key={`level3_${subChildIndex}`}>
                                                                  {navigationItemLink(
                                                                    subChild,
                                                                    `${baseMobileLinkClasses} font-medium`,
                                                                    (event: any) => {
                                                                      handleMobileNavigationItemClick(
                                                                        event,
                                                                        subChild.link,
                                                                        updateAction
                                                                      );
                                                                    }
                                                                  )}
                                                                </li>
                                                              )
                                                            );
                                                          }
                                                        )}
                                                    </ul>
                                                  )
                                                }
                                              ]}
                                              indent={false}
                                              containerClasses="my-4 !px-0 !mx-0"
                                              accordionItemClasses="border-none py-0 px-0 shadow-none rounded-none bg-white"
                                              questionClasses="text-base font-bold"
                                            />
                                          )}
                                        </li>
                                      </div>
                                    );
                                  })}
                                </ul>
                              )}
                            </>
                          )}
                        </li>
                      );
                    })}
                    {isSignedIn && (
                      <li>
                        <h3 className="text-primary-700 font-semibold tracking-wider uppercase text-sm">
                          Your account
                        </h3>
                        <ul className="divide-y divide-deep-100 pt-1 border-b border-deep-200">
                          <li className="py-4 text-base font-bold text-gray">
                            <Link
                              to={`/dashboard/settings`}
                              onClick={(event: any) =>
                                handleMobileNavigationItemClick(event, `/dashboard/settings`, updateAction)
                              }
                              data-cy={`link:view-account`}
                            >
                              Account settings
                            </Link>
                          </li>
                          <li className="py-4 text-base font-bold text-gray">
                            <Link
                              to={location.pathname}
                              onClick={signOut}
                              className="flex space-x-1 items-center"
                              data-cy={`link:view-out`}
                            >
                              <ArrowRightOnRectangleIcon className="w-5" />
                              <span>Log out</span>
                            </Link>
                          </li>
                        </ul>
                      </li>
                    )}
                  </ul>
                  {!isSignedIn && (
                    <div className="sticky bottom-0 p-4 bg-white border-t border-deep-10 shadow-sm">
                      <div className="flex justify-between space-x-4">
                        <Button asChild appearance="primary" className="w-full">
                          <Link to="/login" data-cy={`link:view-log-in`}>
                            Log in
                          </Link>
                        </Button>
                        <Button asChild variant="default" appearance={null} className="w-full">
                          {process.env.GATSBY_SITE_NAME === 'targetjobs' ? (
                            <a href="https://www.targetconnect.com/employers" data-cy={`link:post-job`} target="_blank">
                              <ArrowTopRightOnSquareIcon className="w-4" />
                              <span>Post a job</span>
                            </a>
                          ) : (
                            <Link to="/hiring-organisations" data-cy={`link:post-job`}>
                              Post a job
                            </Link>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Transition.Root>
    </div>
  );

  /**
   * Desktop navigation
   */

  const handleNavigationItemClick = (event: any, to: string, updateAction: AppDispatch) => {
    updateAction({ pathname: to, search: '' }, ActionType.VIEW);
  };

  /**
   * Generates a desktop navigation item
   * @param params { item: NavigationItem, layout: 'inner' | 'outer', depth: number, rootMenuDimenions: any}
   * @return {JSX}
   */
  const NavigationMenuItem = ({
    item,
    layout,
    depth = 0,
    rootMenuDimensions
  }: {
    item: NavigationItem;
    layout: 'outer' | 'inner';
    depth?: number;
    rootMenuDimensions?: DimensionObject;
  }) => {
    const timeoutDuration = 25;
    let timeout: NodeJS.Timeout;
    const useHover = true;
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dropdownRef, dropdownDimensions] = useNavigationLayoutEffect();
    const toggleMenu = () => buttonRef?.current?.click();

    const onMouseHover = (open: boolean, event: Event) => {
      const ancestor = buttonRef?.current?.closest('nav');
      clearTimeout(timeout);
      if (depth === 0) {
        if (event.type.toLowerCase() === 'mouseleave') {
          ancestor?.removeAttribute('mouse-event');
        }
      }
      open &&
        (timeout = setTimeout(() => {
          toggleMenu();
          buttonRef?.current?.blur();
          if (depth === 0) {
            if (event.type.toLowerCase() === 'mouseenter') {
              ancestor?.setAttribute('mouse-event', 'active');
              setTimeout(() => ancestor?.removeAttribute('mouse-event'), 750);
            }
          }
        }, timeoutDuration));
    };

    const LINK_STYLES = cn(
      `hover:text-primary transition duration-200 ease-in-out relative whitespace-nowrap active:ring-0 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:rounded-lg focus:outline-none`
    );

    return item.link ? (
      <ConditionalWrapper
        condition={depth === 0}
        wrapper={(children: any) => <div className="my-3 py-1">{children}</div>}
      >
        {navigationItemLink(
          item,
          cn(
            LINK_STYLES,
            'block',
            layout === 'inner' ? 'px-3 mx-3' : '',
            depth === 0 ? 'text-base font-semibold' : 'font-normal text-sm leading-9'
          ),
          (event: any) => {
            handleNavigationItemClick(event, item.link, updateAction);
          }
        )}
      </ConditionalWrapper>
    ) : (
      <Popover className={cn(layout === 'outer' ? 'relative' : '')}>
        {({ open }) => {
          return (
            <div
              onMouseEnter={(event: any) => useHover && onMouseHover(!open, event)}
              onMouseLeave={(event: any) => useHover && onMouseHover(open, event)}
              className={cn(layout === 'inner' ? 'px-3' : '')}
            >
              <Popover.Button
                onClick={(event: any) => {
                  if (buttonRef?.current?.closest('nav')?.hasAttribute('mouse-event') && depth === 0) {
                    event.preventDefault();
                  }
                }}
                className={cn(
                  'inline-flex w-full items-center justify-between space-x-8 font-semibold transition-colors',
                  open ? 'text-primary' : 'text-gray',
                  depth === 0 ? 'text-base px-3 py-1' : 'text-sm leading-9',
                  LINK_STYLES,
                  layout === 'inner' ? 'px-3' : 'my-3'
                )}
                data-cy={`menu:toggle-${removeNonAlpaChars(item.text)}`}
                ref={buttonRef}
              >
                <span>{item.text}</span>
                {layout === 'inner' && <ChevronRightIcon className={cn('h-4')} />}
              </Popover.Button>
              <Popover.Panel
                static
                className={cn(
                  (layout === 'outer' && 'absolute top-0 -left-3 z-[5] w-fit pointer-events-none') as string,
                  (layout === 'inner' &&
                    'absolute top-0 left-full z-[5] bg-white w-fit before:empty before:bg-white before:h-full before:w-4 before:absolute before:-left-[10px] before:top-0 after:empty after:bg-deep-50 after:h-[calc(100%-24px)] after:w-[1px] after:absolute after:left-0 after:top-3') as string,
                  'shadow-lg rounded-b-lg border-t border-gray-200',
                  depth === 0 ? '[&:not(:has(.sub-menu-open))]:!h-auto' : `${open ? 'sub-menu-open' : ''}`,
                  depth === 0
                    ? open
                      ? 'visible transition opacity-100 duration-150 ease-in'
                      : 'invisible transition opacity-0 duration-200 ease-out pointer-events-none'
                    : open
                    ? 'visible'
                    : 'invisible pointer-events-none'
                )}
                data-depth={depth}
                style={{
                  top: `${layout === 'outer' ? navigationHeightAdjustment : '-1'}px`,
                  minHeight: rootMenuDimensions ? rootMenuDimensions.height || 0 - navigationHeightAdjustment : 'auto'
                }}
                ref={dropdownRef}
              >
                <div
                  className={cn(
                    (layout === 'outer' && 'relative h-full') as string,
                    (layout === 'inner' && 'relative h-auto overflow-y-auto') as string,
                    'bg-white rounded-b-lg py-4 pointer-events-auto max-h-[calc(100vh-5rem)]'
                  )}
                >
                  {item.children &&
                    item.children.map((child: NavigationItem, childIndex: number) => {
                      return (
                        <Popover.Group key={`level${depth}_${childIndex}`}>
                          <NavigationMenuItem
                            item={child}
                            layout="inner"
                            depth={depth + 1}
                            rootMenuDimensions={rootMenuDimensions ?? dropdownDimensions}
                          />
                        </Popover.Group>
                      );
                    })}
                </div>
              </Popover.Panel>
            </div>
          );
        }}
      </Popover>
    );
  };

  const navigationDesktopRender = (
    <nav className="hidden lg:flex lg:w-full lg:space-x-2 xl:space-x-6" data-cy={`section:lg:navigation`}>
      {navigationData.map((item: any, index: number) => {
        return (
          <Popover.Group key={`level0_${index}`}>
            <NavigationMenuItem item={item} layout="outer" />
          </Popover.Group>
        );
      })}
    </nav>
  );

  return (
    <>
      {navigationMobileRender}
      {navigationDesktopRender}
      <UserMatches key="user-matches" />
    </>
  );
};

/**
 * The main nav for the site.
 *
 * @return {JSX}
 */
const Nav = ({ isSignedIn, signOut, updateAction, staticData, authLevel, user, location }: any) => {
  return getNavigation({ staticData, authLevel, user, isSignedIn, signOut, updateAction, location });
};

const mapStateToProps = ({ auth }: any) => {
  return {
    isSignedIn: auth.isSignedIn,
    authLevel: auth.authLevel,
    user: auth.user
  };
};

const mapDispatchToProps = {
  signOut: signOutAction,
  updateAction: updateLastAction
};

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(withLocation(Nav)));
